/**
 * LoadingOverlay - Animated loading spinner with rotating messages
 * 
 * Displays during LLM generation or other async operations.
 * Messages swipe/rotate every ~3s.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface LoadingOverlayProps {
  messages: string[]
}

export function LoadingOverlay({ messages }: LoadingOverlayProps) {
  const { t } = useTranslation()
  const [currentIndex, setCurrentIndex] = useState(0)

  useEffect(() => {
    if (messages.length === 0) return

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % messages.length)
    }, 3000)

    return () => clearInterval(interval)
  }, [messages.length])

  const currentMessage = messages.length > 0 ? messages[currentIndex] : t("common.loading")

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-background/60 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-6 p-8 bg-card pixel-frame-lg">
        {/* Segmented loader: blocks light up in sequence instead of spinning */}
        <div className="flex gap-2" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => (
            <span
              key={i}
              className="h-4 w-4 bg-primary animate-pixel-blink"
              style={{ animationDelay: `${i * 0.2}s` }}
            />
          ))}
        </div>

        {/* Message with swipe animation */}
        <div className="relative h-12 w-80 max-w-[80vw] overflow-hidden">
          <p
            key={currentIndex}
            className="absolute inset-0 flex items-center justify-center text-center font-display text-lg font-bold text-foreground animate-fade-in"
          >
            {currentMessage}
          </p>
        </div>
      </div>
    </div>
  )
}
